var _a, _b, _c;
import { __decorate, __metadata } from "tslib";
import { SKILLSETS_ACCESSOR, SKILLSET_IDS_ACCESSOR, WithSkillsets } from '@common/abilities/shared';
import { UserRoles } from '@core/shared/access';
import { ConfiguratedValidator, CrudEndpoints, EditorDefinition, Expose, IdItemModel, IsIn, IsMatching, IsSafePassword, IsSimpleText, ItemModelDefinition, ItemModelProp, MaxLength, MinLength, ModelId, ToLowerCase, Transform, Trim, Type, ValidateIf, ValidateNested } from '@core/shared/model';
import { UseRestraints } from '@core/shared/restraints';
import { VIRTUAL_PROPS_ACCESSOR, VirtualPropertiesModel } from '@core/shared/virtual-properties';
import { optionalEmailUserItemValidator, usernameOrEmailUserItemValidator } from '../validators';
import { CompanyInterface } from './company.interface';
import { CompanyItem } from './company.item';
import { ReferrerItem } from './referrer.item';
import { UserVirtualPropertyBaseDefinition } from './user-virtual-props-base-definition';
import { UserRestraints } from './user.restraints';
let UserItem = /*#__PURE__*/(() => {
  let UserItem = class UserItem extends IdItemModel {
    static {
      _a = VIRTUAL_PROPS_ACCESSOR, _b = SKILLSETS_ACCESSOR, _c = SKILLSET_IDS_ACCESSOR;
    }
    toString() {
      return "User " + this.username;
    }
  };
  __decorate([Expose(), __metadata("design:type", Number)], UserItem.prototype, "id", void 0);
  __decorate([Expose(), __metadata("design:type", String)], UserItem.prototype, "origin", void 0);
  __decorate([Expose(), __metadata("design:type", String)], UserItem.prototype, "originId", void 0);
  __decorate([Expose(), __metadata("design:type", Object)], UserItem.prototype, "creationDate", void 0);
  __decorate([Expose(), __metadata("design:type", Object)], UserItem.prototype, "updateDate", void 0);
  __decorate([Expose(), ItemModelProp({
    defaultValueCreate: undefined
  }), Type(() => CompanyItem), __metadata("design:type", CompanyInterface)], UserItem.prototype, "company", void 0);
  __decorate([Expose(), __metadata("design:type", Number)], UserItem.prototype, "companyId", void 0);
  __decorate([Expose(), ItemModelProp({
    defaultValueCreate: undefined
  }), Type(() => ReferrerItem), __metadata("design:type", ReferrerItem)], UserItem.prototype, "referrer", void 0);
  __decorate([Expose(), __metadata("design:type", Number)], UserItem.prototype, "referrerId", void 0);
  __decorate([Expose(), __metadata("design:type", String)], UserItem.prototype, "referrerCode", void 0);
  __decorate([Expose(), ItemModelProp({
    defaultValueCreate: undefined
  }), Type(() => CompanyItem), __metadata("design:type", CompanyInterface)], UserItem.prototype, "managing", void 0);
  __decorate([Expose(), __metadata("design:type", Number)], UserItem.prototype, "managingId", void 0);
  __decorate([Expose(), ItemModelProp({
    defaultValueCreate: true
  }), __metadata("design:type", Boolean)], UserItem.prototype, "enabled", void 0);
  __decorate([Expose(), ItemModelProp({}), __metadata("design:type", Boolean)], UserItem.prototype, "isInternal", void 0);
  __decorate([Expose(), __metadata("design:type", String)], UserItem.prototype, "name", void 0);
  __decorate([MinLength(3), MaxLength(64), ConfiguratedValidator(usernameOrEmailUserItemValidator), Trim(), Expose(), ItemModelProp({
    defaultValueCreate: ''
  }), __metadata("design:type", String)], UserItem.prototype, "username", void 0);
  __decorate([IsSimpleText(), MinLength(2), Trim(), Expose(), ItemModelProp({
    defaultValueCreate: ''
  }), __metadata("design:type", String)], UserItem.prototype, "firstName", void 0);
  __decorate([IsSimpleText(), MinLength(2), Trim(), Expose(), ItemModelProp({
    defaultValueCreate: ''
  }), __metadata("design:type", String)], UserItem.prototype, "lastName", void 0);
  __decorate([ItemModelProp({
    defaultValueCreate: '',
    isOptionalUpdate: true,
    allowEmptyString: true
  }), IsSafePassword(), Expose(), __metadata("design:type", String)], UserItem.prototype, "password", void 0);
  __decorate([ValidateIf(e => {
    return typeof e.password === 'string';
  }), IsMatching('password', {
    message: 'Beide Passwörter müssen identisch sein!'
  }), ItemModelProp({
    defaultValueCreate: '',
    isOptionalUpdate: false
  }) // isOptionalUpdate defaults to true which would not run validation at all if field is undefined!
  , Expose(), __metadata("design:type", String)], UserItem.prototype, "password2", void 0);
  __decorate([ConfiguratedValidator(optionalEmailUserItemValidator), Transform(({
    value
  }) => {
    if (typeof value === 'string' && value.trim() === '') return null;else return value;
  }) // required to make it work with unique index
  , Trim(), ToLowerCase(), Expose(), ItemModelProp({
    defaultValueCreate: null
  }), __metadata("design:type", String)], UserItem.prototype, "email", void 0);
  __decorate([Expose(), ItemModelProp({
    defaultValueCreate: ''
  }), __metadata("design:type", String)], UserItem.prototype, "activationCode", void 0);
  __decorate([Expose(), ValidateNested(), __metadata("design:type", Object)], UserItem.prototype, _a, void 0);
  __decorate([Expose(), ItemModelProp({
    defaultValueCreate: null
  }), __metadata("design:type", String)], UserItem.prototype, "notes", void 0);
  __decorate([Expose(), ItemModelProp({
    defaultValueCreate: []
  }), __metadata("design:type", Array)], UserItem.prototype, "optInFeatures", void 0);
  __decorate([Expose(), IsIn(UserRoles, {
    message: 'Es wurde keine gültige Benutzerrolle angegeben!'
  }), __metadata("design:type", String)], UserItem.prototype, "userRole", void 0);
  __decorate([Expose(), __metadata("design:type", Array)], UserItem.prototype, _b, void 0);
  __decorate([Expose(), __metadata("design:type", Array)], UserItem.prototype, _c, void 0);
  UserItem = __decorate([EditorDefinition('manager', {
    companyId: 'readonly',
    userRole: 'readonly',
    managing: 'readonly',
    managingId: 'readonly',
    enabled: true,
    username: true,
    firstName: true,
    lastName: true,
    password: true,
    password2: true,
    email: true,
    optInFeatures: 'readonly',
    [VIRTUAL_PROPS_ACCESSOR]: {
      loadDefinition: 'default'
    }
  }, {
    useCrudEndpoint: 'manager'
  }), EditorDefinition('admin', {
    company: 'readonly',
    companyId: 'readonly',
    referrer: true,
    referrerId: true,
    managing: 'readonly',
    managingId: 'readonly',
    isInternal: 'readonly',
    enabled: true,
    username: true,
    firstName: true,
    lastName: true,
    password: true,
    password2: true,
    email: true,
    [VIRTUAL_PROPS_ACCESSOR]: {
      loadDefinition: 'default'
    },
    [SKILLSET_IDS_ACCESSOR]: true,
    notes: true,
    userRole: 'readonly',
    optInFeatures: true
  }, {
    useCrudEndpoint: 'admin'
  }), EditorDefinition('admin-extended', {
    company: true,
    companyId: true,
    managing: true,
    managingId: true,
    userRole: true
  }, {
    inherit: 'admin'
  }), CrudEndpoints({
    admin: '/admin/users',
    manager: '/manager/users'
  }), UseRestraints(UserRestraints), ItemModelDefinition({
    texts: {
      confirmDeleteItem: item => `Möchten Sie den User "${item.username}" wirklich löschen?<br/><br/> Achtung: Alle Informationen werden unwiderruflich aus dem System entfernt! Bitte sichern Sie zuvor alle noch benötigten Dokumente.`,
      editItem: (item, info) => `User "${item.username}" bearbeiten`,
      item: 'User',
      items: 'User'
    }
  }), VirtualPropertiesModel({
    definition: UserVirtualPropertyBaseDefinition
  }), WithSkillsets({}), ModelId("UserItem") // auto-generated by generate-model-ids
  ], UserItem);
  return UserItem;
})();
export { UserItem };